<script setup lang="ts">
  import { useMenuStore } from '@/composition/use-menu';
  import { useModal } from '@/composition/use-modal';
  import { useRestaurant } from '@/composition/use-restaurant';
  import { OrderPaymentStatus, OrderType } from '@/enums/OrderEnums';
  import { RoundMode } from '@/enums/RoundMode';
  import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
  import { IOrder } from '@/interfaces/IOrder';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { inject, Ref, computed, ref } from 'vue';
  import InputModal from '../../InputModal.vue';
  import { useUi } from '@/composition/use-ui';
  import InvoicingItemService from '@/api/services/invoicing/invoicing-item.service';
  import { usePrinting } from '@/composition/use-printing';
  import ModalPosViewOrderPaymentStatusPayments from './payments/ModalPosViewOrderPaymentStatusPayments.vue';
  import { useDiscounts } from '@/composition/use-discounts';
  import ModalPosViewOrderTips from './ModalPosViewOrderTips.vue';
  import { downloadInvoicePdf } from '@/composition/use-invoice-pdf';
  import { canShareNativePdf } from '@/lib/invoicing/deliver-pdf';
  import { Globals } from '@/modules/Globals';
  import InvoicePdfPreviewModal from '@/components/modals/InvoicePdfPreviewModal.vue';
  import { usePosOrderPayments } from '@/composition/use-pos-order-payments';
  import { isOrderInvoiceable } from '@/utils/orders.utils';
  import { useAuth } from '@/composition/use-auth';
  import { PermissionType } from '@/enums/PermissionType';
  import V2ClubMemberPanel from '@/components/pos-v2/view/club/V2ClubMemberPanel.vue';
  import V2PaymentsBox from '@/components/pos-v2/view/payments/V2PaymentsBox.vue';
  import { ORDER_CLUB_CONTEXT_KEY } from '@/composition/use-order-club-context';

  const restaurantStore = useRestaurantStore();
  const restaurantHelper = useRestaurant();
  const menuStore = useMenuStore();
  const modals = useModal();
  const ui = useUi();
  const printing = usePrinting();
  const discountsHelper = useDiscounts();

  const order = inject<Ref<IOrder>>('order')!;
  // Null in legacy hosts: the pay flows then ask for a code as before.
  const clubContext = inject(ORDER_CLUB_CONTEXT_KEY, null);

  // POS V2 preview modals re-host this tree read-only: hide every mutating
  // action. Interactive contexts (old modal, V2 view tabs) are unaffected.
  const posV2ViewMode = inject<'interactive' | 'preview'>(
    'posV2ViewMode',
    'interactive'
  );
  const isPreview = posV2ViewMode === 'preview';

  // POS V2 hosts (view tabs + previews) get a flat skin instead of the
  // receipt look; the old modal keeps its styling untouched.
  const isPosV2Host = inject<boolean>('posV2Host', false);

  const auth = useAuth();

  // Payments/discounts/tips mutate the order: an OrdersHistoric-only
  // back-office user (unified history route) must not see these actions.
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  const summary = computed(() => {
    const summary = {
      products: 0,
      delivery: 0,
      taxes: 0,
    };

    const o = order.value;

    if (o) {
      summary.products = o.orderItems!.reduce((acc, item) => {
        if (item.canceledAt) return acc;
        return acc + item.total;
      }, 0);

      if (o.type === OrderType.Delivery) {
        summary.delivery = o.deliveryCost ?? 0;
      }

      summary.taxes = o.taxAmount ?? 0;
    }

    return summary;
  });

  const taxName = computed(() => {
    const name = order.value?.taxName ?? 'Impuestos';

    return order.value?.taxMode === TaxDisplayMode.AddAndShow
      ? `${name} (${order.value?.tax}%)`
      : name;
  });

  const showTaxLine = computed(() => {
    return (
      order.value?.taxMode === TaxDisplayMode.AddAndShow ||
      order.value?.taxMode === TaxDisplayMode.AddAtInvoice
    );
  });

  const paidSummary = computed(() => {
    const paid = order.value?.totalPaid ?? 0;
    return {
      paid: paid,
      toPay: (order.value?.total ?? 0) - paid,
    };
  });

  // Payment/discount/tip/invoice actions live in the shared composable so
  // the compact money strip (PosV2ViewMoneyStrip) can trigger the same flows.
  const {
    isPendingOrder,
    doPayment,
    showAddDiscountModal,
    showEditDiscountModal,
    confirmDeleteDiscount,
    clubRedeemAvailable,
    showRedeemPointsModal,
    clubAttachAvailable,
    showTipModal,
    createInvoice,
    invoiceLoading,
    invoiceInProgress,
  } = usePosOrderPayments(order, clubContext);

  const showingPayments = ref(false);
  const showingTips = ref(false);

  const invoicingEnabled = computed(
    () => restaurantStore.restaurant?.country === 'AR'
  );

  // Nothing to invoice: 100% invitación/empleado or zero total (the server
  // rejects those anyway).
  const isInvoiceable = computed(() => isOrderInvoiceable(order.value));

  async function printInvoice() {
    const o = order.value;

    if (!o.invoicingItemId) return;

    try {
      invoiceLoading.value = true;
      const invoicingItem = await InvoicingItemService.getOne(
        o.invoicingItemId
      );

      await printing.printInvoice(invoicingItem);
    } catch (e) {
      ui.serverError(e, 'Ocurrió un error al imprimir la factura');
    } finally {
      invoiceLoading.value = false;
    }
  }

  async function handleDownloadInvoicePdf() {
    const o = order.value;

    if (!o.invoicingItemId) return;

    try {
      invoiceLoading.value = true;
      const invoicingItem = await InvoicingItemService.getOne(
        o.invoicingItemId
      );

      await downloadInvoicePdf({
        invoicingItem,
        orderItems: o.orderItems ?? null,
        order: o,
        branch: restaurantStore.selectedBranch,
        currencySymbol: restaurantStore.currency.symbol,
        openPreview: (blob, filename) => {
          modals.show(
            InvoicePdfPreviewModal,
            { blob, filename },
            {
              name: 'invoice-pdf-preview',
              adaptive: true,
              height: 'auto',
              scrollable: true,
              width: 900,
              maxWidth: window.innerWidth * 0.95,
            }
          );
        },
      });
    } catch (e) {
      ui.serverError(e, 'No se pudo generar la factura en PDF');
    } finally {
      invoiceLoading.value = false;
    }
  }

  const invoiceMenuItems = [
    { title: 'Imprimir factura', icon: 'mdi-printer', click: printInvoice },
  ];
  // Legacy shells (old APKs without Share/Filesystem) can neither share the
  // PDF nor download blobs from the WebView, so hide the action there.
  if (!Globals.isCapacitor || canShareNativePdf()) {
    invoiceMenuItems.push({
      title: 'Descargar PDF',
      icon: 'mdi-file-download-outline',
      click: handleDownloadInvoicePdf,
    });
  }

  const canAddTip = computed(() => {
    const o = order.value;
    return (
      o &&
      o.paymentStatus === 'paid' &&
      o.status !== 'canceled' &&
      o.status !== 'declined'
    );
  });

  // POS V2 hosts show the member panel (figures + Usar puntos / Usar saldo,
  // or the "Identificar socio" row for member-less orders); it replaces the
  // legacy Puntos button. The panel decides itself whether it has anything
  // to show (program coverage, usable resources, permissions).
  const showClubPanel = computed(
    () =>
      isPosV2Host &&
      !isPreview &&
      isPendingOrder.value &&
      (!!order.value.clubMember || clubAttachAvailable.value)
  );
</script>

<template>
  <div class="payment-status-container" :class="{ 'pos-v2-flat': isPosV2Host }">
    <!-- <div class="status-header"></div> -->
    <div class="status-body">
      <div
        class="body-costs"
        :class="{
          'fully-paid': order.paymentStatus === OrderPaymentStatus.Paid,
        }"
      >
        <!-- Bottom serrated edge as a separate element -->
        <div class="bottom-edge"></div>
        <div class="summary-line">
          <div class="summary-title">Productos</div>
          <div class="summary-value">
            {{ restaurantStore.currency.symbol
            }}{{ displayPrice(summary.products) }}
          </div>
        </div>
        <div v-if="order.type === OrderType.Delivery" class="summary-line">
          <div class="summary-title">Costo de envío</div>
          <div class="summary-value">
            {{ restaurantStore.currency.symbol
            }}{{ displayPrice(summary.delivery) }}
          </div>
        </div>
        <div v-if="showTaxLine" class="summary-line">
          <div class="summary-title">{{ taxName }}</div>
          <div class="summary-value">
            {{ restaurantStore.currency.symbol
            }}{{ displayPrice(summary.taxes) }}
          </div>
        </div>

        <!-- New discounts section -->
        <div
          v-if="order.discounts && order.discounts.length > 0"
          class="discounts-section"
        >
          <div class="discounts-header">
            <span class="discounts-title">Descuentos</span>
          </div>
          <div
            v-for="discount in order.discounts"
            :key="discount.id"
            class="discount-line"
          >
            <div class="discount-info">
              <span class="discount-label">{{
                discountsHelper.formatDiscountLabel(discount)
              }}</span>
              <span class="discount-value"
                >-{{
                  discountsHelper.formatDiscountValue(
                    discount,
                    restaurantStore.currency.symbol
                  )
                }}</span
              >
            </div>
            <div
              v-if="isPendingOrder && !isPreview && canManageOrders"
              class="discount-actions"
            >
              <!-- Coupon and club discounts are not editable: their value is
                   tied to the coupon rules / the member's points ledger. -->
              <v-btn
                v-if="discount.type !== 'coupon' && discount.type !== 'club'"
                icon
                x-small
                @click="showEditDiscountModal(discount)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon x-small @click="confirmDeleteDiscount(discount)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Legacy discount display for backwards compatibility -->
        <div v-else-if="order.discount" class="summary-line">
          <div class="summary-title">Descuento (%{{ order.discount }})</div>
          <div class="summary-value">
            -{{ restaurantStore.currency.symbol
            }}{{ displayPrice(order.discountAmount) }}
          </div>
        </div>

        <!-- Show total discounted amount if using new system -->
        <div
          v-if="order.discountedAmount && order.discountedAmount > 0"
          class="summary-line"
        >
          <div class="summary-title">Total descuentos</div>
          <div class="summary-value tw-text-red-600">
            -{{ restaurantStore.currency.symbol
            }}{{ displayPrice(order.discountedAmount) }}
          </div>
        </div>

        <div class="summary-line total">
          <div class="summary-title">
            <span>Total</span>
            <span
              v-if="order?.roundMode !== RoundMode.None"
              class="summary-note"
              >Incluye redondeo</span
            >
          </div>
          <div class="summary-value">
            {{ restaurantStore.currency.symbol
            }}{{ displayPrice(order?.total ?? 0) }}
          </div>
        </div>

        <!-- Tips integrated in costs section -->
        <div
          v-if="order.tipTotal > 0 || (canAddTip && !isPreview)"
          class="tips-section"
        >
          <div class="summary-line tips-line">
            <div class="summary-title">
              <span>Propinas</span>
              <v-btn
                v-if="order.tipTotal > 0"
                icon
                x-small
                @click="showingTips = !showingTips"
                class="tw-ml-1"
              >
                <v-icon small>
                  {{ showingTips ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-btn>
            </div>
            <div class="summary-value tw-text-green-600">
              +{{ restaurantStore.currency.symbol
              }}{{ displayPrice(order.tipTotal || 0) }}
            </div>
          </div>

          <!-- Tip details dropdown -->
          <ModalPosViewOrderTips
            v-if="showingTips"
            :show-header="false"
            :show-summary="false"
            class="tw-mt-2"
          />

          <div v-if="order.tipTotal > 0" class="summary-line grand-total">
            <div class="summary-title tw-font-bold">Total con propina</div>
            <div class="summary-value tw-font-bold">
              {{ restaurantStore.currency.symbol
              }}{{ displayPrice((order?.total ?? 0) + (order?.tipTotal ?? 0)) }}
            </div>
          </div>
        </div>

        <!-- POS V2 hosts fold the payments into the PAGADO tile; the legacy
             modal keeps the collapsible "Pagos" line + paid summary. -->
        <V2PaymentsBox v-if="isPosV2Host" />
        <template v-else>
          <ModalPosViewOrderPaymentStatusPayments
            v-if="order.totalPaid > 0"
            :order="order"
            :showing-payments="showingPayments"
            @update:showingPayments="showingPayments = $event"
          />
          <div class="paid-summary">
            <div class="paid-section">
              <div class="summary-label">PAGADO</div>
              <div class="summary-amount paid-amount">
                {{ restaurantStore.currency.symbol
                }}{{ displayPrice(paidSummary.paid) }}
              </div>
            </div>
            <div class="to-pay-section">
              <div class="summary-label">FALTA PAGAR</div>
              <div class="summary-amount to-pay-amount">
                {{ restaurantStore.currency.symbol
                }}{{ displayPrice(paidSummary.toPay) }}
              </div>
            </div>
          </div>
        </template>
      </div>
      <V2ClubMemberPanel v-if="showClubPanel" />
      <!-- Invoice actions stay visible without OrdersManage (legacy back-office
           parity: Historic users invoice from history); the order-mutating
           actions (Descuento/Puntos/Propina/Pagar) require OrdersManage. -->
      <div
        v-if="
          isPendingOrder &&
          !isPreview &&
          (canManageOrders || order.paymentStatus === OrderPaymentStatus.Paid)
        "
        class="body-actions"
      >
        <!-- Pagar y Aplicar descuento y Agregar propina -->
        <div v-if="canManageOrders" class="sub-actions">
          <v-btn
            color="blue"
            dark
            class="action-button"
            small
            depressed
            outlined
            @click="showAddDiscountModal"
          >
            <v-icon left>mdi-tag-outline</v-icon>
            Descuento
          </v-btn>
          <v-btn
            v-if="clubRedeemAvailable && !showClubPanel"
            color="orange"
            dark
            class="action-button"
            small
            depressed
            outlined
            @click="showRedeemPointsModal"
          >
            <v-icon left>mdi-star-circle-outline</v-icon>
            Puntos
          </v-btn>
          <v-btn
            color="success"
            dark
            class="action-button"
            small
            depressed
            outlined
            @click="showTipModal"
          >
            <v-icon left>mdi-cash</v-icon>
            Propina
          </v-btn>
        </div>
        <v-btn
          v-if="
            order.paymentStatus !== OrderPaymentStatus.Paid && canManageOrders
          "
          color="success"
          class="action-button"
          depressed
          @click="doPayment"
        >
          <v-icon left>mdi-cash-register</v-icon>
          Pagar
        </v-btn>
        <template v-if="order.paymentStatus === OrderPaymentStatus.Paid">
          <v-btn
            v-if="!order.invoicingItemId && isInvoiceable"
            color="info"
            class="action-button"
            depressed
            :loading="invoiceLoading"
            :disabled="!invoicingEnabled || invoiceInProgress"
            @click="createInvoice"
          >
            <v-icon left>{{
              invoiceInProgress ? 'mdi-timer-sand' : 'mdi-receipt'
            }}</v-icon>
            {{ invoiceInProgress ? 'Factura en proceso' : 'Facturar' }}
          </v-btn>
          <div v-else-if="order.invoicingItemId" class="invoice-split">
            <v-btn
              color="info"
              class="invoice-split-main"
              depressed
              :loading="invoiceLoading"
              @click="printInvoice"
            >
              <v-icon left>mdi-printer</v-icon>
              <!-- Short label: the column is ~400px wide and shared with
                   Descuento/Propina, "Imprimir factura" never fits. -->
              <span class="invoice-split-label">Factura</span>
            </v-btn>
            <v-menu offset-y top left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="info"
                  class="invoice-split-toggle"
                  depressed
                  :disabled="invoiceLoading"
                  aria-label="Más opciones de factura"
                  v-on="on"
                  v-bind="attrs"
                >
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="item in invoiceMenuItems"
                  :key="item.title"
                  @click="item.click"
                >
                  <v-list-item-icon>
                    <v-icon>{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </div>
    </div>
    <!-- <div class="status-footer"></div> -->
  </div>
</template>

<style scoped lang="scss">
  .payment-status-container {
    @apply tw-space-y-2;

    .status-body {
      @apply tw-space-y-2;

      .body-costs {
        @apply tw-bg-green-50 tw-rounded-lg tw-p-4 tw-space-y-1 tw-relative;
        @apply tw-border-2 tw-border-dashed tw-border-green-200;
        overflow: hidden;

        // PAGADO watermark
        &.fully-paid::before {
          content: 'PAGADO';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-30deg);
          font-size: 4rem;
          font-weight: bold;
          font-family: 'Courier New', monospace;
          color: rgba(34, 197, 94, 0.15); // Green with low opacity
          letter-spacing: 0.5rem;
          pointer-events: none;
          z-index: 1;
        }

        // Ensure content is above watermark
        > * {
          position: relative;
          z-index: 2;
        }

        // Top serrated edge
        &::after {
          content: '';
          position: absolute;
          display: block;
          height: 10px;
          top: -10px;
          left: 0;
          right: 0;
          background: linear-gradient(
              45deg,
              transparent 33.333%,
              #f0fdf4 33.333%,
              #f0fdf4 66.667%,
              transparent 66.667%
            ),
            linear-gradient(
              -45deg,
              transparent 33.333%,
              #f0fdf4 33.333%,
              #f0fdf4 66.667%,
              transparent 66.667%
            );
          background-size: 20px 20px;
          background-position: 0 -10px;
          z-index: 3;
        }

        // Bottom serrated edge element
        .bottom-edge {
          position: absolute;
          height: 10px;
          bottom: -10px;
          left: 0;
          right: 0;
          background: linear-gradient(
              135deg,
              transparent 33.333%,
              #f0fdf4 33.333%,
              #f0fdf4 66.667%,
              transparent 66.667%
            ),
            linear-gradient(
              -135deg,
              transparent 33.333%,
              #f0fdf4 33.333%,
              #f0fdf4 66.667%,
              transparent 66.667%
            );
          background-size: 20px 20px;
          background-position: 0 0;
          z-index: 3;
        }

        .summary-line {
          @apply tw-flex tw-justify-between tw-text-gray-700 tw-text-sm;
          font-family: 'Courier New', monospace;

          &.total {
            @apply tw-text-gray-900 tw-font-bold tw-border-t-2 tw-border-dashed tw-border-gray-300 tw-pt-2 tw-mt-2;
          }

          &.tips-line {
            @apply tw-border-t-2 tw-border-dashed tw-border-gray-300 tw-pt-2 tw-mt-2;

            .summary-title {
              @apply tw-flex tw-items-center;
            }

            .summary-value {
              @apply tw-text-green-700 tw-font-semibold;
            }
          }

          &.grand-total {
            @apply tw-border-t-4 tw-border-double tw-border-gray-400 tw-pt-2 tw-mt-2;

            .summary-value {
              @apply tw-text-gray-900;
            }
          }

          &.vertical {
            @apply tw-flex-col tw-items-start;
          }

          .summary-title {
            @apply tw-font-medium tw-uppercase tw-text-xs tw-tracking-wider;
          }

          .summary-note {
            @apply tw-text-xs tw-text-gray-500 tw-font-normal tw-ml-1 tw-normal-case tw-tracking-normal;
          }

          .summary-value {
            @apply tw-font-mono;
          }
        }

        .tips-section {
          @apply tw-space-y-1;
        }

        .discounts-section {
          @apply tw-space-y-1 tw-border-t-2 tw-border-dashed tw-border-gray-300 tw-pt-2 tw-mt-2;

          .discounts-header {
            @apply tw-flex tw-justify-between tw-items-center;

            .discounts-title {
              @apply tw-font-medium tw-uppercase tw-text-xs tw-tracking-wider tw-text-gray-700;
            }
          }

          .discount-line {
            @apply tw-flex tw-justify-between tw-items-center tw-text-sm;
            font-family: 'Courier New', monospace;

            .discount-info {
              @apply tw-flex tw-justify-between tw-flex-1 tw-mr-2;

              .discount-label {
                @apply tw-text-gray-600 tw-text-xs;
              }

              .discount-value {
                @apply tw-text-red-600 tw-font-medium tw-font-mono;
              }
            }

            .discount-actions {
              @apply tw-flex tw-space-x-1;
            }
          }
        }

        .paid-summary {
          @apply tw-flex tw-justify-between tw-gap-4 tw-pt-3 tw-mt-3;
          @apply tw-bg-yellow-50 tw-rounded-lg tw-p-3 -tw-mx-1;

          .paid-section {
            @apply tw-flex-1;
          }

          .to-pay-section {
            @apply tw-flex-1 tw-text-right;
          }

          .summary-label {
            @apply tw-font-bold tw-uppercase tw-text-xs tw-tracking-wider tw-text-gray-700 tw-mb-1;
            font-family: 'Courier New', monospace;
          }

          .summary-amount {
            @apply tw-font-mono tw-text-lg tw-font-bold;

            &.paid-amount {
              @apply tw-text-green-700;
            }

            &.to-pay-amount {
              @apply tw-text-red-700;
              @apply tw-text-xl; // Make it even bigger to stand out
            }
          }
        }
      }

      .body-actions {
        @apply tw-flex tw-space-x-2;

        .action-button {
          @apply tw-flex-1;
        }

        .sub-actions {
          @apply tw-flex tw-flex-col tw-space-y-2;
          @apply sm:tw-flex-row sm:tw-space-y-0 sm:tw-space-x-2;

          .action-button {
            @apply tw-flex-none sm:tw-flex-1;
          }
        }

        .invoice-split {
          @apply tw-flex tw-flex-1 tw-min-w-0;

          .invoice-split-main {
            @apply tw-flex-1 tw-min-w-0 tw-rounded-r-none tw-px-2;

            /* Vuetify sets flex: 1 0 auto on the content, which prevents it
               from shrinking: without this the label clips on both sides
               instead of truncating. */
            ::v-deep .v-btn__content {
              @apply tw-min-w-0 tw-flex-shrink;

              .v-icon {
                @apply tw-flex-none;
              }
            }

            .invoice-split-label {
              @apply tw-truncate;
            }
          }

          .invoice-split-toggle {
            @apply tw-rounded-l-none tw-px-0;
            min-width: 36px !important;
            /* Divider so the group visibly reads as two buttons */
            border-left: 1px solid rgba(255, 255, 255, 0.4) !important;
          }
        }
      }
    }

    // Flat skin for POS V2 hosts: no receipt cosplay (serrated edges,
    // Courier, dashed green) — quiet neutral card that matches the V2 kit.
    &.pos-v2-flat {
      .status-body .body-costs {
        @apply tw-border-0 tw-bg-transparent tw-p-0;

        &::after {
          display: none;
        }

        .bottom-edge {
          display: none;
        }

        // Paid at a glance: soft emerald wash over the whole costs block,
        // with the PAGADO watermark reading through the totals box below.
        &.fully-paid {
          @apply tw-bg-emerald-50/70 tw-p-3;

          &::before {
            font-family: inherit;
            color: rgba(5, 150, 105, 0.14);
          }
        }

        .summary-line {
          font-family: inherit;
          @apply tw-text-neutral-600;

          &.total {
            @apply tw-border-t tw-border-solid tw-border-neutral-200 tw-text-neutral-900;
          }

          &.tips-line {
            @apply tw-border-t tw-border-solid tw-border-neutral-200;

            .summary-value {
              @apply tw-text-emerald-600;
            }
          }

          &.grand-total {
            @apply tw-border-t-2 tw-border-solid tw-border-neutral-300;

            .summary-value {
              @apply tw-text-neutral-900;
            }
          }

          .summary-note {
            @apply tw-text-neutral-400;
          }

          .summary-value {
            font-family: inherit;
            @apply tw-tabular-nums;
          }
        }

        .discounts-section {
          @apply tw-border-t tw-border-solid tw-border-neutral-200;

          .discounts-header .discounts-title {
            @apply tw-text-neutral-500;
          }

          .discount-line {
            font-family: inherit;

            .discount-info {
              .discount-label {
                @apply tw-text-neutral-500;
              }

              .discount-value {
                font-family: inherit;
                @apply tw-tabular-nums;
              }
            }
          }
        }

        // The box root carries this scope id. Specificity ties with the
        // `.body-costs` tw-space-y-1 rule (which would force 4px), so this
        // wins by source order only: it must stay below the base rules.
        .v2-payments-box {
          @apply tw-mt-3;
        }
      }
    }
  }
</style>
